import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Lightbulb } from 'lucide-react';
import { warmEase } from '@/shared/motion/motionPresets';
import type { NavigatorCategory } from './navigator-results.types';

type NavigatorSpecialtyIntroProps = {
  category: NavigatorCategory;
};

/** "an Elder Law Attorney" vs "a Home Care Provider" — good enough for the catalog names. */
const articleFor = (name: string) => (/^[aeiou]/i.test(name.trim()) ? 'an' : 'a');

/**
 * Specialty intro strip — the "why you need this specialty" highlighted
 * summary at the top of each tab panel (feature 080). Two clamped lines by
 * default; "More" reveals the full copy plus the quieter what-they-do block.
 */
export const NavigatorSpecialtyIntro = ({ category }: NavigatorSpecialtyIntroProps) => {
  const [expanded, setExpanded] = useState(false);
  const why = category.whyYouNeedThem?.trim();
  if (!why) return null;

  const whatTheyDo = category.whatTheyDo?.trim();
  // The toggle earns its place only when it reveals something: the quiet
  // what-they-do block, or copy long enough to be clamped at two lines.
  // ponytail: 90 chars ≈ two mobile lines of text-sm; upgrade path is
  // measuring clamp overflow with a ref if the copy lengths ever get close.
  const showToggle = Boolean(whatTheyDo) || why.length > 90;

  return (
    <section className="rounded-2xl bg-sage-bg px-5 py-4">
      <div className="flex items-start gap-4">
        <span
          className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-card-base text-sage shadow-[0_1px_3px_rgba(15,23,42,0.08)]"
          aria-hidden
        >
          <Lightbulb className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-sage">
            Why {articleFor(category.name)} {category.name}?
          </p>
          <p
            className={`mt-1 text-sm leading-relaxed text-text-primary ${
              expanded ? '' : 'line-clamp-2'
            }`}
          >
            {why}
          </p>

          <AnimatePresence initial={false}>
            {expanded && whatTheyDo && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: warmEase }}
                className="overflow-hidden"
              >
                <p className="mt-2 border-t border-sage/15 pt-2 text-sm leading-relaxed text-text-secondary">
                  {whatTheyDo}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {showToggle && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              aria-expanded={expanded}
              className="mt-1.5 text-xs font-medium text-sage transition-colors duration-150 ease-out hover:text-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              {expanded ? 'Less' : 'More'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
